<template>
  <div id="presentation-root">
    <mdb-mask class="overflow-auto gp-full-overlay" overlay="stylish-strong">
      <g-p-draggable v-if="showAllControls && currentState
              && presentation && currentState.status == 'Running'" base-div-id="player-controls">
        <mdb-card class="player-controls-header">
          <mdb-card-header class="secondary-color mt-2">
            All Controls
          </mdb-card-header>
          <mdb-card-body class="text-center">
            <remote-control v-if="presentation && currentState"
                            :current-state="currentState"
                            :presentation="presentation"
                            @terminate="exitModal=true"/>

            <div>
              <mdb-btn aria-label="Information" color="blue"
                       icon="info" size="lg" title="Information"
                       @click.native="showSessionInfo = !showSessionInfo"
              />
              <mdb-btn :icon="notificationSounds ? 'volume-slash' : 'volume'" aria-label="Notification Sounds"
                       color="blue" size="lg" title="Notification Sounds"
                       @click="toggleNotificationSounds"
              />
              <mdb-btn aria-label="New Window" color="red" icon="external-link-alt"
                       size="lg"
                       title="Load Controls in separate window" @click.native="newWinControls"
              />
              <mdb-btn @click="showAllControls=false">Close</mdb-btn>
            </div>
          </mdb-card-body>
        </mdb-card>
      </g-p-draggable>

      <div v-if="currentState && currentState.type=='YT'" class="gp-has-header">

        <youtube id="youtube-container" ref="youtube" :player-vars="playerParams" :video-id="currentState.youTubeId"
                 fit-parent
                 style="display:none;"
                 @ended="videoEnded"
                 @error="videoError" @paused="videoPaused"
                 @playing="videoPlaying" @ready="videoReady"></youtube>

        <div v-if="presentation.isHost && currentState.openToUser">
          <mdb-btn icon="pause" @click="sendVideoCommand('pause')">Pause Video for Everyone</mdb-btn>
        </div>
      </div>

      <mdb-container class="gp-has-header">
        <mdb-card v-if="showError">
          <mdb-card-header>
            <h3>Oops!</h3>
          </mdb-card-header>
          <mdb-card-body class="text-center">
            <sumato/>
            <h3>{{ errorMessage }}</h3>
          </mdb-card-body>
        </mdb-card>

        <mdb-card v-if="connectionFailed">
          <mdb-card-header>
            <h3>{{ presentation.name }}</h3>
          </mdb-card-header>
          <mdb-card-body>
            <h4>ERROR</h4>
            {{ connectionMessage }}
          </mdb-card-body>
        </mdb-card>

        <mdb-card v-if="terminated">
          <mdb-card-header>
            <h3>Session Ended</h3>
          </mdb-card-header>
          <mdb-card-body>
            <sumato/>
            <h2>Thanks for participating!</h2>
          </mdb-card-body>
        </mdb-card>

        <mdb-card v-if="connectionClosed && !terminated" class="mb-4">
          <mdb-card-header>
            <h3>Connection Closed</h3>
          </mdb-card-header>
          <mdb-card-body>
            <div>It looks like your connection to the presentation server has timed out.</div>
            <div>
              <mdb-btn icon="play" @click="loadPresentation">Reconnect</mdb-btn>
            </div>
          </mdb-card-body>
        </mdb-card>

        <mdb-card v-if="presentation && !connectionConfirmed && !connectionFailed">
          <mdb-card-body>
            <h3>{{ presentation.name }}</h3>
            <div class="text-info">Hosted by {{ presentation.host }}</div>
            <mdb-spinner/>
            <h2>One Moment...connecting to presentation</h2>
            <div class="mt-4">
              <avatar :avatar="presentation.groupAvatar" container-class="text-center"/>
              <h3>{{ presentation.groupName }}</h3>
              <div class="text-info small">Powered by Ezumee Sumato&trade;</div>
            </div>
          </mdb-card-body>
        </mdb-card>
        <mdb-btn-fixed v-if="presentation && connectionConfirmed && currentState && !terminated && !connectionClosed"
                       :bottom="5"
                       :right="10"
                       icon="cogs"
                       size="md" style="z-index:2000;" @mouseenter.native="buttonsHover"
                       @mouseleave.native="buttonsHoverOut">
          <mdb-btn-fixed-item v-if="!presentation.isHost" :icon="notificationSounds ? 'volume-slash' : 'volume'"
                              :show="showButtons" aria-label="Notification Sounds"
                              color="blue"
                              size="sm"
                              title="Notification Sounds" @click.native="toggleNotificationSounds"/>
          <mdb-btn-fixed-item v-if="!presentation.isHost" :show="showButtons" aria-label="Leave" color="red"
                              icon="portal-exit"
                              size="sm"
                              title="Leave/Quit" @click.native="exitModal = true"
          />
          <mdb-btn-fixed-item v-if="!presentation.isHost && currentState.status == 'Running'" :show="showButtons"
                              aria-label="Information"
                              color="blue"
                              icon="info" size="sm" title="Information"
                              @click.native="showSessionInfo = !showSessionInfo"
          />
          <mdb-btn-fixed-item v-if="presentation.isHost" :show="showButtons"
                              aria-label="More Options" icon="gamepad"
                              size="sm" title="More"
                              @click.native="showAllControls= !showAllControls"/>

          <mdb-btn-fixed-item v-if="presentation.isHost && currentState.status == 'Running'" :show="showButtons"
                              aria-label="Next" icon="arrow-left"
                              size="sm" title="Previous"
                              @click.native="controls.cardChange(presentation,'previous')"/>
          <mdb-btn-fixed-item v-if="presentation.isHost && currentState.status == 'Running'" :show="showButtons"
                              icon="arrow-right" size="sm"
                              @click.native="controls.cardChange(presentation,'next')"/>
          <mdb-btn-fixed-item v-if="presentation.isHost && currentState &&
                                       !currentState.openToUser && currentState.status == 'Running'
                                       && (currentState.type == 'QC' || currentState.type=='QF')" :show="showButtons"
                              aria-label="Let Them Answer"
                              color="warning" icon="question"
                              size="sm" title="Let Them Answer"
                              @click.native="controls.openToAnswer(presentation, true)"/>
          <mdb-btn-fixed-item v-if="presentation.isHost && currentState &&
                                       !currentState.openToUser && currentState.status == 'Running'
                                       && currentState.type == 'SR'" :show="showButtons" aria-label="Show the Reveal"
                              color="warning" icon="presentation"
                              size="sm" title="Show the Reveal"
                              @click.native="controls.openToAnswer(presentation, true)"/>

        </mdb-btn-fixed>

        <div v-if="presentation && connectionConfirmed && currentState && !terminated && !hidePresentationArea">
          <mdb-jumbotron v-if="currentState.status=='Paused'" class="mb-0 text-center blue-grey lighten-5">
            <h1 class="card-title h2 indigo-text">{{ presentation.name }}</h1>
            <div class="text-info">Hosted by {{ presentation.host }}</div>
            <h2 class="card-title h2">
              <mdb-icon icon="pause"/>
            </h2>

            <p class="my-4 font-weight-bold">Presentation is currently paused.</p>
            <mdb-btn v-if="presentation.isHost" icon="play" @click="controls.togglePause(presentation)">Resume</mdb-btn>
            <mdb-btn v-if="presentation.isHost" icon="folder-open" @click="switchPresentation">Switch Presentation
            </mdb-btn>
            <div class="mt-4">
              <avatar :avatar="presentation.groupAvatar" container-class="text-center"/>
              <h3>{{ presentation.groupName }}</h3>
              <div class="text-info small">Powered by Ezumee Sumato&trade;</div>
            </div>

          </mdb-jumbotron>

          <mdb-card v-if="currentState.status=='Running'" class="jumbo-page mb-4">
            <mdb-card-body v-if="myAnswer && !currentState.openToUser && (currentState.type == 'QC' || currentState.type == 'QF')" class="text-center">
              <h4>You Answered</h4>
              <h3 class="text-info">{{ myAnswer }}</h3>
            </mdb-card-body>

            <mdb-card-body class="text-left" v-html="currentState.currentPageText"/>
            <mdb-card-body v-if="(currentState.type=='IC')" class="text-center">
              <div v-for="(ans,i) in currentState.possibleAnswers" :key="i">
                <mdb-btn @click="selectAnswer(ans)">{{ ans }}
                  <mdb-icon v-if="myAnswer == ans" icon="check"/>
                </mdb-btn>

              </div>
            </mdb-card-body>
            <mdb-card-body v-if="(currentState.type=='YT' && currentState.youTubeId) || currentState.type == 'EV'">
              <div v-if="!currentState.openToUser && currentState.type == 'YT'">
                <img :src="settings.staticImageUrl+'/youtube_play.png'" style="max-height: 50%"/>
              </div>
              <div v-if="!currentState.openToUser && !presentation.isHost">
                <div>
                  <mdb-spinner/>
                </div>
                <h4 v-if="!videoComplete">Video will start shortly</h4>
                <h4 v-if="videoComplete">Waiting on presenter and other participants.</h4>
              </div>
              <div v-if="presentation.isHost && currentState.openToUser">
                <mdb-btn icon="pause" @click="sendVideoCommand('pause')">Pause Video for Everyone</mdb-btn>
              </div>
              <div v-if="presentation.isHost && !currentState.openToUser">
                <div>Start the video when all participants are ready.</div>
                <div>
                  <mdb-btn icon="play" @click="sendVideoCommand('play')">Start Video for Everyone</mdb-btn>
                </div>
                <div>
                  <mdb-btn icon="play" @click="openVideo">Play Video On This Screen Only</mdb-btn>
                </div>
              </div>
            </mdb-card-body>
          </mdb-card>
        </div>

        <mdb-modal
          v-if="currentState && currentState.status=='Running' &&
                (currentState.type == 'YT' || currentState.type == 'EV')
                && allAnswers.length>0 && (presentation.isHost || !currentState.openToUser)"
          info
          position="bottom-left" removeBackdrop scrollable side
          size="lg">
          <div :close=false class="secondary-color mt-2">
            Participant Status
            <a v-if="!readinessHidden" class="close" href="" @click.prevent="readinessHidden=true">
              <mdb-icon icon="eye-slash"/>
            </a>
            <a v-if="readinessHidden" class="close" href="" @click.prevent="readinessHidden=false">
              <mdb-icon icon="eye"/>
            </a>
          </div>
          <mdb-modal-body v-if="!readinessHidden" class="text-left">
            <div v-for="(answer,i) in allAnswers" :key="i+100" class="text-left" md="1">
              <mdb-badge color="secondary">
                <mdb-icon v-if="answer.answer=='video-ended'" color="danger" icon="stop"/>
                <mdb-icon v-if="answer.answer=='video-paused'" color="warning" icon="pause"/>
                <mdb-icon v-if="answer.answer =='video-playing'" color="success" icon="play"/>
                <i>{{ answer.name }}</i> {{ answer.time }}
              </mdb-badge>
            </div>
          </mdb-modal-body>
        </mdb-modal>

        <mdb-modal
          v-if="currentState && (currentState.type=='YT' || currentState.type == 'EV')
                && currentState.youTubeId && presentation.isHost && !currentState.openToUser"
          info position="bottom-left" removeBackdrop scrollable side
          size="lg">
          <div :close="false" class="secondary-color pt-2">
            Player Status
            <a v-if="!readinessHidden" class="close" href="" @click.prevent="readinessHidden=true">
              <mdb-icon icon="eye-slash"/>
            </a>
            <a v-if="readinessHidden" class="close" href="" @click.prevent="readinessHidden=false">
              <mdb-icon icon="eye"/>
            </a>
          </div>
          <mdb-modal-body v-if="!readinessHidden" class="text-left">
            <div v-for="(a,i) in currentState.attendees" :key="i">
              <mdb-icon v-if="a.youTubePlayerReady" color="success" icon="dot-circle"/>
              <mdb-icon v-if="!a.youTubePlayerReady" color="warning" icon="dot-circle"/>
              {{ a.name }}
            </div>
          </mdb-modal-body>
        </mdb-modal>

        <mdb-modal
          v-if="currentState && currentState.type=='EV' && presentation.isHost && !currentState.openToUser"
          info position="bottom-left" removeBackdrop scrollable side
          size="lg">
          <div :close="false" class="secondary-color pt-2">
            Browser Video Status
            <a v-if="!readinessHidden" class="close" href="" @click.prevent="readinessHidden=true">
              <mdb-icon icon="eye-slash"/>
            </a>
            <a v-if="readinessHidden" class="close" href="" @click.prevent="readinessHidden=false">
              <mdb-icon icon="eye"/>
            </a>
          </div>
          <mdb-modal-body v-if="!readinessHidden" class="text-left">
            <div v-for="(a,i) in currentState.attendees" :key="i">
              <mdb-icon v-if="a.youTubePlayerReady" color="success" icon="dot-circle"/>
              <mdb-icon v-if="!a.youTubePlayerReady" color="warning" icon="dot-circle"/>
              {{ a.name }}
            </div>
          </mdb-modal-body>
        </mdb-modal>

        <mdb-modal
          v-if="presentation && currentState && currentState.type!='YT' && currentState.type != 'EV'
          && presentation.isHost && allAnswers.length>0"
          info position="bottom-left" removeBackdrop scrollable side
          size="lg">
          <mdb-modal-header :close=false class="secondary-color">
            <h4>Participant Answers</h4>
            <a v-if="!readinessHidden" class="close" href="" @click.prevent="readinessHidden=true">
              <mdb-icon icon="eye-slash"/>
            </a>
            <a v-if="readinessHidden" class="close" href="" @click.prevent="readinessHidden=false">
              <mdb-icon icon="eye"/>
            </a>
          </mdb-modal-header>
          <mdb-modal-body v-if="!readinessHidden" class="text-left">
            <div v-for="(answer,i) in allAnswers" :key="i" class="text-left mb-4">
              <div class="text-info small">{{ answer.name }}</div>
              <div>{{ answer.answer }}</div>
            </div>
          </mdb-modal-body>
        </mdb-modal>

        <mdb-modal v-if="showCallingOn" centered class="text-center animated rotateIn" elegant
                   removeBackdrop size="sm">
          <mdb-modal-header :close="false" center class="secondary-color">
            <h3>Calling On....</h3>
          </mdb-modal-header>
          <mdb-modal-body>
            <h1>{{ callingOn }}</h1>
          </mdb-modal-body>
        </mdb-modal>

        <mdb-modal
          v-if="currentState && currentState.openToUser && currentState.type != 'YT' && currentState.type != 'EV'"
          class="text-center animated slideInLeft"
          removeBackdrop
          scrollable size="lg">
          <mdb-modal-header v-if="currentState.type != 'SR'" :close="false" center class="secondary-color">
            <h3 v-if="presentation.isHost">Users Are Answering</h3>
            <h3 v-if="!presentation.isHost && !answered">Your Answer</h3>
            <h3 v-if="!presentation.isHost && answered">Thank You!</h3>
          </mdb-modal-header>
          <mdb-modal-body>
            <div v-if="!presentation.isHost && answered">
              <mdb-spinner/>
              <div class="text-info">Waiting on Others/Presenter Working</div>
              <div class="text-info">You answered:</div>
              <h3 class="text-success">{{ myAnswer }}</h3>
              <mdb-btn icon="pencil" size="sm" @click="answered=false">Edit</mdb-btn>
              <div v-for="(answer,i) in allAnswers" :key="i" class="text-left">
                <avatar :avatar="answer.avatar" :name="answer.name" :size="50"/>
                {{ answer.name }}
                <mdb-icon icon="check"/>
                <div style="clear:both;"/>
              </div>
            </div>
            <div v-if="!presentation.isHost && !answered && currentState.type == 'QC'">
              <div v-for="(ans,i) in currentState.possibleAnswers" :key="i">
                <mdb-btn @click="selectAnswer(ans)">{{ ans }}</mdb-btn>
              </div>
            </div>
            <div v-if="!presentation.isHost && !answered && currentState.type == 'QF'">
              <mdb-input v-model="myAnswer" :lines="5" label="My Answer" type="textarea"
                         @keydown.native="handleAnswerKeydown"/>
              <mdb-btn icon="check" @click="selectAnswer(myAnswer)">Submit</mdb-btn>
            </div>
            <div v-if="currentState.type == 'SR'">
              <div v-html="currentState.revealText"/>
              <div v-if="presentation.isHost">
                <mdb-btn color="danger"
                         icon="door-closed"
                         @click.native="controls.openToAnswer(presentation, false)">Close the Reveal
                </mdb-btn>
              </div>

            </div>
            <div v-if="presentation.isHost && currentState.type != 'SR'">
              <mdb-spinner/>
              <div>
                <mdb-btn color="danger"
                         icon="door-closed"
                         @click.native="controls.openToAnswer(presentation, false)">Answer Time is Done
                </mdb-btn>
              </div>
            </div>

          </mdb-modal-body>
        </mdb-modal>

        <mdb-modal v-if="showGuestModal"
                   class="text-center" size="sm">
          <mdb-modal-header :close="false" center class="secondary-color">
            <h3>Welcome, Guest. Please enter your name.</h3>
          </mdb-modal-header>
          <mdb-modal-body>
            <mdb-input id="newName" v-model="newName"
                       active label="Name"
                       @keydown.native="newNameKeyDown"
            />
            <mdb-btn :disabled="!newName" @click="setName">
              <mdb-icon icon="save"/>
              Save
            </mdb-btn>
          </mdb-modal-body>
        </mdb-modal>


        <mdb-modal v-if="passwordDialog"
                   class="text-center" size="sm">
          <mdb-modal-header :close="false" center class="secondary-color">
            <h3>Password Required</h3>
          </mdb-modal-header>
          <mdb-modal-body>
            <mdb-input id="password" v-model="joinPassword"
                       active label="Password"
                       @keydown.native="passwordKeyDown"
            />
            <mdb-btn :disabled="!joinPassword" @click="loadPresentation">
              <mdb-icon icon="check"/>
              Join
            </mdb-btn>
          </mdb-modal-body>
        </mdb-modal>

        <mdb-modal v-if="showNavigator" class="text-left" size="lg" @close="showNavigator=false">
          <mdb-modal-header :close="true" center class="secondary-color">
            <h3>Pick a New Presentation</h3>
          </mdb-modal-header>
          <mdb-modal-body>
            <presentation-navigator :group="presentation.groupId" @selectedPresentation="newPresentationSelected"/>
          </mdb-modal-body>
        </mdb-modal>

        <mdb-modal v-if="showSessionInfo" class="text-left" scrollable size="lg" @close="showSessionInfo=false">
          <mdb-modal-header :close="true" center class="secondary-color">
            <h3>{{ presentation.name }} Information</h3>
          </mdb-modal-header>
          <mdb-modal-body>
            <mdb-card class="mb-2">
              <mdb-card-header class="text-primary text-white">
                <h5>Joining Details</h5>
              </mdb-card-header>
              <mdb-card-body>
                <div>URL: <span class="text-info">
                  <a :href="settings.frontEndUrl + '/presentation-viewer/' + presentation.sessionId "
                     @click.prevent="ignoreClick">{{ settings.frontEndUrl }}/presentation-viewer/{{presentation.sessionId}}
                  </a></span></div>
                <div v-if="presentation.password">Password: <span class="text-info">{{ presentation.password }}</span>
                </div>
                <qrcode-vue :value="settings.frontEndUrl + '/presentation-viewer/'+ presentation.sessionId" :size="300" level="H"></qrcode-vue>
                <div v-if="presentation.quickLink">
                  <div>Quick Link: <span class="text-info">
                    <a href="https://ezumee.link"
                       @click.prevent="ignoreClick">https://ezumee.link</a></span>
                  </div>
                  <div><span class="h3">Code: </span> <span class="text-info">{{ presentation.quickLink }}</span></div>
                  <mdb-btn v-if="presentation.isHost" color="danger" icon="trash" size="sm" @click="cancelQuickLink" rounded>Cancel Quick Link</mdb-btn>
                </div>

                <div v-if="!presentation.quickLink && settings.siteName=='Ezumee' && presentation.isHost">
                  <mdb-row>
                    <mdb-col col="6">
                      <mdb-input placeholder="Quick Link Code" v-model="quickLink" label="Create a Quick Link"/>
                    </mdb-col>
                    <mdb-col col="6" style="padding-top:1.5em;">
                        <mdb-btn size="sm" rounded :disabled="!quickLink" @click="createQuickLink" icon="link">Create</mdb-btn>
                    </mdb-col>
                  </mdb-row>
                </div>


              </mdb-card-body>
            </mdb-card>
            <mdb-card class="mb-2">
              <mdb-card-header class="text-primary text-white">
                <h5>Participants</h5>
              </mdb-card-header>
              <mdb-card-body>
                <mdb-row>
                  <mdb-col v-for="a in currentState.attendees" :key="a.id" col="2">
                    <avatar :avatar="a.avatar" :name="a.name" :size="50" container-class=""/>
                    {{ a.name }}
                  </mdb-col>
                </mdb-row>
              </mdb-card-body>
            </mdb-card>
          </mdb-modal-body>
        </mdb-modal>
        <yes-no-modal v-if="exitModal"
                      :additional-text="presentation.isHost ? 'This will end the session for everyone.' : ''"
                      question="Are you sure you want to leave this presentation?"
                      @no="exitModal=false" @yes="terminateSession"/>
      </mdb-container>
    </mdb-mask>
  </div>
</template>
<script>
import api from '../../services/groupsPlaceApi.js'
import Avatar from "../common/Avatar";
import presentationControls from "../../services/presentationControls";
import {
  mdbBadge,
  mdbBtn,
  mdbBtnFixed,
  mdbBtnFixedItem,
  mdbCard,
  mdbCardBody,
  mdbCardFooter,
  mdbCardHeader,
  mdbChipInput,
  mdbCol,
  mdbContainer,
  mdbIcon,
  mdbInput,
  mdbJumbotron,
  mdbMask,
  mdbModal,
  mdbModalBody,
  mdbModalHeader,
  mdbModalTitle,
  mdbRow,
  mdbSpinner
} from 'mdbvue';
import YesNoModal from "../common/YesNoModal";
import Vue from "vue";
import Sumato from "../Sumato";
import PresentationNavigator from "./Navigator";
import GPDraggable from "../common/GPDraggable";
import RemoteControl from "./RemoteControl";
import QrcodeVue from "qrcode.vue";

export default {
  name: "presentation-viewer",
  components: {
    YesNoModal,
    mdbContainer,
    mdbBtn,
    mdbCard,
    mdbCardHeader,
    mdbCardBody,
    mdbCardFooter,
    mdbIcon,
    mdbInput,
    mdbModal,
    mdbModalHeader,
    mdbModalBody,
    mdbModalTitle,
    mdbChipInput,
    mdbMask,
    mdbBadge,
    mdbSpinner,
    mdbJumbotron,
    mdbBtnFixed,
    mdbBtnFixedItem,
    mdbRow,
    mdbCol,
    Avatar,
    Sumato,
    PresentationNavigator,
    GPDraggable,
    RemoteControl,
    QrcodeVue
  },
  computed: {
    loggedIn() {
      return this.$store.state.loggedIn
    },
    currentUser() {
      return this.$store.state.userDetails
    },
    settings() {
      return this.$store.state.settings
    },
    hidePresentationArea() {
      if (!this.currentState) return false
      if ((this.currentState.type == 'YT' | this.currentState.type == 'EZ') && this.currentState.openToUser) return true
      return false
    },
    lastError() {
      if (!this.presentation) return null
      else return this.presentation.lastError
    }
  },
  watch: {
    lastError(n) {
      if (n) this.handleLastError()
    }
  },
  data() {
    return {
      connectionClosed: false,
      presentation: null,
      pageClass: null,
      socketConnection: null,
      connectionConfirmed: false,
      connectionFailed: false,
      connectionMessage: null,
      currentState: {status: "", openToUser: false, attendees: []},
      showSessionInfo: false,
      callingOn: "",
      showCallingOn: false,
      exitModal: false,
      terminated: false,
      joinPassword: null,
      passwordDialog: false,
      showButtons: false,
      showAllControls: false,
      showGuestModal: false,
      newName: "",
      myAnswer: "",
      answered: false,
      allAnswers: [],
      showError: false,
      errorMessage: null,
      showNavigator: false,
      notificationSounds: true,
      playerParams: {
        autoplay: 0,
        controls: 1,
        disablekb: 1,
        enablejsapi: 1,
        modestbranding: 1,
        start: 0
      },
      player: null,
      showControls: false,
      playing: false,
      readinessHidden: false,
      videoComplete: false,
      videoId: null,
      controls: null,
      remoteWindow: null,
      quickLink: ""
    }
  },
  mounted() {
    let savedNot = localStorage.getItem("notificationSounds")
    this.notificationSounds = savedNot == "false" ? false : true
    this.loadPresentation()
    this.controls = presentationControls
  },
  beforeRouteLeave(to, from, next) {
    this.terminated = true
    if (this.socketConnection) this.socketConnection.close()
    next()
    return
  },
  methods: {
    handleLastError() {
      this.$notify.error({message: this.presentation.lastError, timeOut: 3000})
      this.presentation.lastError = null
    },
    toggleNotificationSounds() {
      this.notificationSounds = this.notificationSounds ? false : true
      localStorage.setItem("notificationSounds", this.notificationSounds)
    },
    openVideo() {
      if (this.currentState.type == 'YT') {
        this.toggleYouTubeContainer("block")
        this.player.playVideo()
      }
      if (this.currentState.type == 'EV') this.ezPlay()
    },
    sendVideoCommand(command) {
      this.$store.dispatch("setExecuting", true)
      api.post("/presentationViewer/sendVideoCommand/" + this.$route.params.sessionId, {
        token: this.presentation.accessToken, command: command
      }).then(
        () => {

        },
        (error) => {
          this.answered = false
          this.$notify.error({message: error.response.data, timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })

    },
    createQuickLink() {
      this.$store.dispatch("setExecuting", true)
      api.post("/presentationViewer/quickLink/" + this.$route.params.sessionId, {
        token: this.presentation.accessToken, linkCode: this.quickLink
      }).then(
        () => {
          this.presentation.quickLink = this.quickLink
        },
        (error) => {
          this.answered = false
          this.$notify.error({message: error.response.data, timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })

    },
    cancelQuickLink() {
      this.$store.dispatch("setExecuting", true)
      api.post("/presentationViewer/cancelQuickLink/" + this.$route.params.sessionId, {
        token: this.presentation.accessToken
      }).then(
        () => {
          this.presentation.quickLink = null
        },
        (error) => {
          this.answered = false
          this.$notify.error({message: error.response.data, timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })

    },
    videoReady(x) {
      this.playerParams.origin = this.settings.frontEndUrl
      this.player = this.$refs.youtube.player
      this.setVideoReady()
    },
    setVideoReady() {
      api.post("/presentationViewer/userVideoReady/" + this.$route.params.sessionId, {
        token: this.presentation.accessToken
      }).then(
        () => {

        },
        (error) => {
          this.answered = false
          this.$notify.error({message: error.response.data, timeOut: 5000})
        })
    },
    toggleControls() {
      if (this.playing) {
        this.toggleYouTubeContainer("none")
        this.player.pauseVideo()
      } else {
        this.player.playVideo()
        this.toggleYouTubeContainer("block")
      }
    },
    videoPlaying(x) {
      this.playing = true
      this.selectAnswer("video-playing")
      this.videoComplete = false
      setTimeout(() => {
        this.trackTime()
      }, 5000)

    },
    trackTime() {
      if (this.player && this.playing && this.player.currentTime) {
        this.selectAnswer("video-playing:" + this.player.currentTime)
        setTimeout(() => {
          this.trackTime()
        }, 5000)
      }
    },
    videoPaused(x) {
      this.playing = false
      if (this.currentState.openToUser) this.selectAnswer("video-paused")
    },
    videoError(x) {
      this.playing = false
      console.log("Video Error", x)
    },
    videoEnded(x) {
      this.playing = false
      this.selectAnswer("video-ended")
      this.videoComplete = true
      if (!this.presentation.isHost) {
        this.currentState.openToUser = false
      }
    },
    switchPresentation() {
      this.showNavigator = true
    },
    buttonsHover() {
      this.showButtons = true;
    },
    buttonsHoverOut() {
      this.showButtons = false;
    },
    newPresentationSelected(pres) {
      this.showNavigator = false
      this.$store.dispatch("setExecuting", true)
      let url = "/presentation/switchPresentation/" + this.$route.params.sessionId
      api.post(url, {presentation: pres.id}).then(
        (results) => {

        },
        (error) => {
          this.$notify.error({messageText: error.response.data, timeOut: 3000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })

    },
    loadPresentation() {
      this.passwordDialog = false
      this.connectionClosed = false
      this.$store.dispatch("setExecuting", true)
      let lastToken = localStorage.getItem("lastPresentationToken")
      let lastGuestName = localStorage.getItem("lastGuestName")
      let url = "/presentationViewer/start/" + this.$route.params.sessionId
      if (lastToken) url += "?lastToken=" + lastToken
      api.post(url, {password: this.joinPassword, name: lastGuestName}).then(
        (results) => {
          this.presentation = results.data
          if (results.data.accessToken) localStorage.setItem("lastPresentationToken", results.data.accessToken)
          else localStorage.removeItem("lastPresentationToken")
          this.setBackground()
          this.connectSocket()
          if (this.presentation.isGuest && this.presentation.participationName == 'Guest') {
            this.showGuestModal = true
            Vue.nextTick().then(function () {
              let el = document.getElementById("newName")
              if (el) el.focus()
            })
          }
        },
        (error) => {
          if (error.response.status == 401) {
            this.passwordDialog = true
          } else {
            this.errorMessage = error.response.data
            this.showError = true
          }
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    ignoreClick() {
      return
    },
    connectSocket() {
      if (!this.presentation.socketsURL) {
        this.connectionFailed = true
        this.connectionMessage = "A configuration is missing to enable communications for this service."
        return
      }
      try {
        this.socketConnection = new WebSocket("wss://" + this.presentation.socketsURL)
      } catch (e) {
        this.connectionFailed = true
        this.connectionMessage = "Could not connect to presentation services. " + e
        return
      }

      this.socketConnection.onmessage = (event) => {
        let eventData = JSON.parse(event.data)
        if (eventData.action == "getConnectionId") {
          api.post("/presentationViewer/storeSocketConnection/" + this.$route.params.sessionId, {
            token: this.presentation.accessToken,
            connectionId: eventData.connectionId
          }).then(
            (results) => {
              this.connectionConfirmed = true
              this.currentState = results.data
              if ((this.currentState.type == 'QF' || this.currentState.type == 'QC') && this.presentation.isHost) {
                this.allAnswers = []
                for (let a of this.currentState.attendees) {
                  this.allAnswers.push({id: a.id, avatar: a.avatar, name: a.name, answer: a.lastAnswer})
                }
              }
              if (this.currentState.type == 'EV') {
                this.checkEzVideo()
              }
            },
            (error) => {
              this.$notify.error({message: error.response.data, timeOut: 5000})
            })
        } else {
          this.handleSocketMessage(eventData)
        }
      }

      this.socketConnection.onclose = (event) => {
        this.connectionClosed = true
      }

      this.socketConnection.onopen = (event) => {
        let payload = {action: "sendmessage", innerAction: "confirmConnection", data: ""}
        this.socketConnection.send(JSON.stringify(payload))
      }
    },
    playJoinSound() {
      if (!this.notificationSounds) return
      const audio = new Audio(this.settings.staticImageUrl + "/arrive.wav");
      audio.volume = 0.30
      audio.play();
    },
    playDisconnectSound() {
      if (!this.notificationSounds) return
      const audio = new Audio(this.settings.staticImageUrl + "/leave.wav");
      audio.volume = 0.30
      audio.play();
    },
    setBackground() {
      let el = document.getElementById("presentation-root")
      if (el) {
        if (!this.presentation.background) {
          el.removeAttribute("style")
        } else {
          el.setAttribute("style", "background-image: url('" + this.presentation.background + "'); background-size: cover;")
        }
      }
    },
    handleSocketMessage(event) {
      if (!this.currentState) return
      if (event.innerAction == "userConnected") {
        let loc = -1
        for (let i = 0; i < this.currentState.attendees.length; i++) {
          if (this.currentState.attendees[i].id == event.id) {
            loc = i
          }
        }
        if (loc == -1) {
          this.currentState.attendees.push({id: event.id, name: event.name, avatar: event.avatar})
          if (event.name != "Guest") {
            this.$notify.success({
                                   message: event.name + " has joined.",
                                   timeOut: 1500,
                                   position: "bottom center"
                                 })
            this.playJoinSound()
          }
        }
      }
      if (event.innerAction == "userDisconnected") {
        let loc = -1
        for (let i = 0; i < this.allAnswers.length; i++) {
          let a = this.allAnswers[i]
          if (event.id == a.id) {
            loc = i
          }
        }
        if (loc >= 0) this.allAnswers.splice(loc, 1)

        loc = -1
        for (let i = 0; i < this.currentState.attendees.length; i++) {
          if (this.currentState.attendees[i].id == event.id) {
            this.playDisconnectSound()
            this.$notify.warn({
                                message: this.currentState.attendees[i].name + " has left.",
                                timeOut: 1500,
                                position: "bottom center"
                              })
            loc = i
          }
        }
        if (loc >= 0) this.currentState.attendees.splice(loc, 1)
      }
      if (event.innerAction == "guestNameChange") {
        let loc = -1
        for (let i = 0; i < this.currentState.attendees.length; i++) {
          if (this.currentState.attendees[i].id == event.id) {
            loc = i
            this.currentState.attendees[i].name = event.name
            this.playJoinSound()
            this.$notify.success({message: event.name + " has joined.", timeOut: 1500, position: "bottom center"})
          }
        }
      }
      if (event.innerAction == "sessionTerminated") {
        localStorage.removeItem("lastPresentationToken")
        this.socketConnection.close()
        this.terminated = true
        this.presentation = null
        this.currentState = null
        if (this.remoteWindow) this.remoteWindow.close()
      }
      if (event.innerAction == "setStatus") {
        this.currentState.status = event.status
      }
      if (event.innerAction == "newPage") {
        this.currentState.currentPageText = event.currentPageText
        this.currentState.currentPageId = event.currentPageId
        this.currentState.currentPageName = event.currentPageName
        this.currentState.openToUser = event.openToUser
        this.currentState.type = event.type
        this.currentState.youTubeId = event.youTubeId
        if (event.possibleAnswers) this.currentState.possibleAnswers = event.possibleAnswers
        else this.currentState.possibleAnswers = []

        if (event.revealText) this.currentState.revealText = event.revealText
        else this.currentState.revealText = ""
        this.answered = false
        this.myAnswer = ""
        this.allAnswers = []
        this.videoComplete = false
        if (event.type == 'EV') {
          this.checkEzVideo()
        }
        for (let i = 0; i < this.currentState.attendees.length; i++) {
          let a = this.currentState.attendees[i]
          a.youTubePlayerReady = false
        }
        window.scrollTo(0,0)
      }
      if (event.innerAction == "presentationSwitched") {
        this.presentation.name = event.name
        this.currentState = event.currentState
        this.presentation.background = event.background
        this.setBackground()
        if (event.currentState.possibleAnswers) this.currentState.possibleAnswers = event.currentState.possibleAnswers
        else this.currentState.possibleAnswers = []
        if (event.currentState.revealText) this.currentState.revealText = event.currentState.revealText
        else this.currentState.revealText = ""
        this.answered = false
        this.myAnswer = ""
        this.currentState.openToUser = false
        this.allAnswers = []
      }
      if (event.innerAction == "userPlayerReady") {
        for (let i = 0; i < this.currentState.attendees.length; i++) {
          let a = this.currentState.attendees[i]
          if (a.id == event.id) a.youTubePlayerReady = true
        }
      }
      if (event.innerAction == "userAnswered") {
        if (!this.allAnswers) this.allAnswers = []
        let matched = false
        for (let i = 0; i < this.allAnswers.length; i++) {
          let a = this.allAnswers[i]
          if (event.answer.id == a.id) {
            this.allAnswers[i].answer = event.answer.answer
            this.allAnswers[i].time = event.answer.time
            matched = true
          }
        }
        for (let i = 0; i < this.currentState.attendees.length; i++) {
          let a = this.currentState.attendees[i]
          if (a.id == event.answer.id) a.lastAnswer = event.answer.answer
        }
        if (!matched) this.allAnswers.push(event.answer)
      }
      if (event.innerAction == "openToUser") {
        this.currentState.openToUser = true
        this.answered = false
        this.allAnswers = []
      }
      if (event.innerAction == "closeToUser") {
        this.currentState.openToUser = false
        this.answered = false
      }
      if (event.innerAction == "callOn") {
        this.callingOn = event.name
        this.showCallingOn = true
        setTimeout(() => {
          this.showCallingOn = false
        }, 2000)
      }

      if (event.innerAction == "videoCommand") {
        if (event.command == "play") {
          this.currentState.openToUser = true
          if (this.currentState.type == 'YT') {
            this.toggleYouTubeContainer("block")
            this.player.playVideo()
          }
          if (this.currentState.type == 'EV') this.ezPlay()

        }
        if (event.command == "pause" && this.player) {
          this.currentState.openToUser = false
          if (this.currentState.type == 'YT') {
            this.toggleYouTubeContainer("none")
            this.player.pauseVideo()
          }
          if (this.currentState.type == 'EV') this.ezPause()
        }
      }
    },
    checkEzVideo() {
      Vue.nextTick().then(() => {
        this.player = document.getElementById("ez-video-container")
        if (this.player && typeof this.player.canPlayType == "function") {
          if (this.player.canPlayType) this.setVideoReady()
          else return
          this.player.onplaying = () => {
            this.videoPlaying()
          }
          this.player.onpause = () => {
            this.videoPaused()
          }
          this.player.onended = () => {
            this.videoEnded()
          }
          if (this.currentState.openToUser) {
            this.ezPlay()
          }
        }
      })

    },
    ezPlay() {
      this.player.play()
      this.player.controls = true
    },
    ezPause() {
      this.player.pause()
      this.player.controls = false
    },
    toggleYouTubeContainer(displayStyle) {
      /*vue's reactivity doesn't seem to work with what YouTube is
       doing with the iframe, so we will do it the old-fashioned way
       */
      let el = document.getElementById("youtube-container")
      if (el) {
        el.style.display = displayStyle;
      }

    },
    handleAnswerKeydown(e) {
      if (e.keyCode == 13 && this.myAnswer) this.selectAnswer(this.myAnswer)
    },
    newNameKeyDown(e) {
      if (e.keyCode == 13 && this.newName) this.setName()
    },
    passwordKeyDown(e) {
      if (e.keyCode == 13 && this.joinPassword) this.loadPresentation()
    },
    setName() {
      localStorage.setItem("lastGuestName", this.newName)
      this.$store.dispatch("setExecuting", true)
      this.presentation.participationName = this.newName
      this.showGuestModal = false
      api.post("/presentationViewer/setName/" + this.$route.params.sessionId, {
        token: this.presentation.accessToken,
        name: this.newName
      }).then(
        () => {

        },
        (error) => {
          this.showGuestModal = true
          this.$notify.error({message: error.response.data, timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    selectAnswer(myAnswer) {
      this.answered = true
      api.post("/presentationViewer/userAnswer/" + this.$route.params.sessionId, {
        token: this.presentation.accessToken, answer: myAnswer
      }).then(
        () => {
          this.myAnswer = myAnswer
        },
        (error) => {
          this.answered = false
          this.$notify.error({message: error.response.data, timeOut: 5000})
        })
    },
    terminateSession() {
      this.exitModal = false
      this.controls.terminateSession(this.presentation)
      this.presentation = null
      this.currentState = null
      this.terminated = true
      this.socketConnection.close()
    },
    newWinControls() {
      this.showAllControls = false
      this.remoteWindow = window.open("/presentation-remote/" + this.presentation.sessionId,
                                      "Controls", "menubar=no,location=no,status=no,height=350,width=450")
    }
  }
};

</script>
<style scoped>
.jumbo-page {
  display: block;
  overflow: auto;
}

</style>
